import { useEffect, useState } from "react";
import { useI18n } from "@wato/i18n/react";
import { Button } from "@wato/ui";
import { clearSupportToken, getSupportToken, SUPPORT_HEADER } from "./support-session";

export interface SupportSessionInfo {
  readonly workspaceName: string;
  readonly expiresAt: string;
  readonly serverNow: string;
}

/** Persistent "you are browsing as support" banner (ACT-13). Pure: data + end() are injected. */
export function SupportBanner({
  load,
  end,
  onEnded,
  tick = 15_000,
}: {
  readonly load: () => Promise<SupportSessionInfo>;
  readonly end: () => Promise<void>;
  readonly onEnded: () => void;
  readonly tick?: number;
}) {
  const { t } = useI18n();
  const [info, setInfo] = useState<{ name: string; expiresMs: number; skewMs: number } | null>(
    null,
  );
  const [now, setNow] = useState(Date.now());
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    load().then(
      (s) => {
        if (!alive) return;
        setInfo({
          name: s.workspaceName,
          expiresMs: Date.parse(s.expiresAt),
          skewMs: Date.parse(s.serverNow) - Date.now(),
        });
      },
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), tick);
    return () => clearInterval(id);
  }, [tick]);

  const minutes = info
    ? Math.max(0, Math.ceil((info.expiresMs - (now + info.skewMs)) / 60_000))
    : 0;
  const over = failed || (info !== null && minutes <= 0);

  return (
    <div
      role="status"
      data-testid="support-banner"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 bg-warning-bg px-4 py-2 text-sm font-medium text-fg"
    >
      <span>
        {over
          ? t("supportSession.banner.expired")
          : info
            ? t("supportSession.banner.text", { workspace: info.name, minutes })
            : ""}
      </span>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          void end().finally(() => {
            clearSupportToken();
            onEnded();
          });
        }}
      >
        {t("supportSession.banner.end")}
      </Button>
    </div>
  );
}

/** Mounted by the workspace shell: renders only when this tab carries a support token. */
export function SupportBannerGate() {
  const token = getSupportToken();
  if (!token) return null;
  const call = (method: string, path: string) =>
    fetch(path, { method, credentials: "omit", headers: { [SUPPORT_HEADER]: token } });
  return (
    <SupportBanner
      load={async () => {
        const res = await call("GET", "/v1/support-session/current");
        if (!res.ok) throw new Error("support session ended");
        return (await res.json()) as SupportSessionInfo;
      }}
      end={async () => {
        await call("POST", "/v1/support-session/end");
      }}
      onEnded={() => {
        globalThis.location.assign("/");
      }}
    />
  );
}
